import { useState } from 'react';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlaylistAddCheck from '@mui/icons-material/PlaylistAddCheck';
import ExtensionIcon from '@mui/icons-material/Extension';
import { Menu } from 'common/ui/Menu';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { ReactComponent as SettingIconSVG } from 'assets/settingIcon.svg';
import { CHROME_EXTENSION_INSTALL_PAGE } from 'config/constants';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import type { PositioningStrategy } from '@popperjs/core/lib/types';
import { useDisplaySystemCheck } from 'common/SystemCheck/useSystemCheck';
import { SironaWorkspaceInfo } from './SironaWorkspaceInfo';

type MenuProfilePopperProps = React.PropsWithChildren<{
  displayName: Nullable<string>;
  email: Nullable<string>;
  clinicName: Nullable<string>;
  clinicSmid: Nullable<string>;
  onSettingClick?: () => void;
  onLogoutClick: () => void;
  visible?: boolean;
  strategy?: PositioningStrategy;
  showSettingsPane?: boolean;
}>;

type ProfileHeaderProps = {
  displayName: Nullable<string>;
  email: Nullable<string>;
  clinicName: Nullable<string>;
  clinicSmid: Nullable<string>;
};

export function ProfileHeader({ displayName, email, clinicName, clinicSmid }: ProfileHeaderProps) {
  return (
    <ListSubheader
      css="background-color: transparent; line-height: normal; padding-bottom: 1rem;"
      data-testid="profile-header"
    >
      {displayName}
      {email && (
        <Typography variant="caption" display="block" color="text.secondary">
          {email}
        </Typography>
      )}
      {clinicName && (
        <Typography
          variant="caption"
          display="block"
          color="text.secondary"
          onClick={() => {
            if (clinicSmid) {
              navigator.clipboard.writeText(clinicSmid);
            }
          }}
          sx={{
            cursor: clinicSmid ? 'pointer' : 'default',
          }}
        >
          <strong>Clinic:</strong> {clinicName}
          {clinicSmid && (
            <ContentCopyIcon sx={{ fontSize: 10, ml: 0.75, verticalAlign: 'middle' }} />
          )}
        </Typography>
      )}
    </ListSubheader>
  );
}

export const MenuProfilePopper: React.ComponentType<MenuProfilePopperProps> = ({
  displayName,
  email,
  clinicName,
  clinicSmid,
  onSettingClick,
  onLogoutClick,
  children,
  visible,
  strategy = 'absolute',
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const { showSystemCheckScreen } = useDisplaySystemCheck();

  const onClose = () => {
    setShowDialog(false);
  };
  return (
    <>
      <Menu
        trigger="click"
        offset={[0, 15]}
        placement="right-start"
        visible={visible}
        strategy={strategy}
        data-testid="menu-profile"
        render={() => (
          <List
            dense
            subheader={
              <ProfileHeader
                displayName={displayName}
                email={email}
                clinicName={clinicName}
                clinicSmid={clinicSmid}
              />
            }
            component="nav"
            sx={{ width: '21rem' }}
            data-testid="menu-profile-list"
          >
            <ListItemButton onClick={() => setShowDialog(true)}>
              <ListItemText>About Sirona Workspace</ListItemText>
            </ListItemButton>
            <Divider sx={{ margin: '1rem 0' }} />
            {onSettingClick && (
              <ListItemButton onClick={onSettingClick} data-testid="profile-settings">
                <ListItemIcon>
                  <SettingIconSVG />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </ListItemButton>
            )}
            {!isWorkspaceManagerAvailable() && (
              <a
                css="all: unset; display: content;"
                target="_blank"
                rel="noreferrer noopener"
                href={CHROME_EXTENSION_INSTALL_PAGE}
              >
                <ListItemButton>
                  <ListItemIcon>
                    <ExtensionIcon />
                  </ListItemIcon>
                  <ListItemText>Install extension</ListItemText>
                </ListItemButton>
              </a>
            )}
            {onSettingClick && (
              <ListItemButton onClick={showSystemCheckScreen}>
                <ListItemIcon>
                  <PlaylistAddCheck />
                </ListItemIcon>
                <ListItemText>System check</ListItemText>
              </ListItemButton>
            )}
            <ListItemButton onClick={onLogoutClick} data-testid="signout">
              <ListItemIcon>
                <PowerSettingsNewIcon />
              </ListItemIcon>
              <ListItemText>Sign out</ListItemText>
            </ListItemButton>
          </List>
        )}
      >
        {children}
      </Menu>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={showDialog}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 398,
              p: '0.5rem 3rem 3rem 3rem',
            },
          },
        }}
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            sx={{
              position: 'absolute',
              right: '1rem',
              top: '1rem',
            }}
            onClick={onClose}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SironaWorkspaceInfo />
        </DialogContent>
      </Dialog>
    </>
  );
};
